import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2, Copy, MessageCircle, ShieldCheck, Sparkles, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Confetti() {
  // CSS-only falling particles
  const pieces = Array.from({ length: 40 });
  const colors = ["#05F29B", "#ffffff", "#5B9EFF", "#FFD166", "#F49FD0"];
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden z-0" aria-hidden>
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 2;
        const duration = 3 + Math.random() * 3;
        const size = 6 + Math.random() * 8;
        const color = colors[i % colors.length];
        const rotate = Math.random() * 360;
        return (
          <span
            key={i}
            className="absolute top-[-30px] rounded-sm"
            style={{
              left: `${left}%`,
              width: size,
              height: size * 0.4,
              backgroundColor: color,
              transform: `rotate(${rotate}deg)`,
              animation: `confetti ${duration}s linear ${delay}s infinite`,
              opacity: 0.85,
            }}
          />
        );
      })}
    </div>
  );
}

export default function ThankYou() {
  const [cfg, setCfg] = useState(null);
  const [token, setToken] = useState(null);
  const [shortCode, setShortCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    const load = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const [c, t] = await Promise.all([
          axios.get(`${API}/thankyou/config`),
          axios.post(`${API}/thankyou/tokens`, {
            source: params.get("source") || "lastlink",
            reference: params.get("ref") || params.get("order") || params.get("id") || null,
          }),
        ]);
        setCfg(c.data);
        setToken(t.data.token);
        setShortCode(t.data.short_code);
      } catch (e) {
        // graceful fallback
        setCfg({
          title: "Parabéns!",
          subtitle: "Seu pagamento foi confirmado",
          body_text: "Você adquiriu seu Ritual e sua participação foi confirmada com sucesso!",
          whatsapp_url: "",
          button_text: "Entrar no meu WhatsApp",
          show_token: true,
          company_name: "",
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      /* ignore */
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#050708]">
        <Loader2 className="w-8 h-8 animate-spin text-[#05F29B]" />
      </div>
    );
  }

  const title = cfg?.title || "Parabéns!";
  const subtitle = cfg?.subtitle || "Seu pagamento foi confirmado";
  const body = cfg?.body_text || "";
  const buttonText = cfg?.button_text || "Entrar no meu WhatsApp";
  const waUrl = cfg?.whatsapp_url || "";
  const company = cfg?.company_name || "";

  return (
    <div
      className="min-h-screen relative overflow-hidden text-white grain-bg"
      style={{
        background:
          "radial-gradient(1200px 600px at 50% -10%, rgba(5, 242, 155, 0.18), transparent 60%), radial-gradient(800px 500px at 80% 100%, rgba(91, 158, 255, 0.10), transparent 60%), #050708",
      }}
      data-testid="thankyou-page"
    >
      <style>{`
        @keyframes confetti {
          0% { transform: translateY(-20px) rotate(0deg); opacity: 0; }
          10% { opacity: 0.9; }
          100% { transform: translateY(105vh) rotate(720deg); opacity: 0.5; }
        }
        @keyframes checkmarkPop {
          0% { transform: scale(0); opacity: 0; }
          60% { transform: scale(1.2); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes ringPulse {
          0% { transform: scale(0.7); opacity: 0.8; }
          80%, 100% { transform: scale(1.8); opacity: 0; }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes wabtnPulse {
          0% { box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.55), 0 10px 40px -10px rgba(37, 211, 102, 0.5); }
          70% { box-shadow: 0 0 0 22px rgba(37, 211, 102, 0), 0 10px 40px -10px rgba(37, 211, 102, 0.5); }
          100% { box-shadow: 0 0 0 0 rgba(37, 211, 102, 0), 0 10px 40px -10px rgba(37, 211, 102, 0.5); }
        }
        @keyframes wabtnBob {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        @keyframes shine {
          0% { transform: translateX(-120%); }
          100% { transform: translateX(220%); }
        }
        .fade-up { animation: fadeUp 0.7s cubic-bezier(0.2,0.8,0.2,1) both; }
      `}</style>

      <Confetti />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-5 py-16">
        {/* Top brand */}
        {company && (
          <div
            className="fade-up mb-8 text-[11px] uppercase tracking-[0.3em] text-white/50"
            style={{ animationDelay: "0.05s" }}
          >
            {company}
          </div>
        )}

        {/* Checkmark */}
        <div className="relative mb-8" data-testid="success-icon">
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background: "rgba(5, 242, 155, 0.25)",
              animation: "ringPulse 1.6s ease-out 0.3s infinite",
            }}
          />
          <div
            className="relative w-28 h-28 rounded-full grid place-items-center"
            style={{
              background:
                "radial-gradient(circle at 30% 30%, #0df0a1, #05c47e)",
              boxShadow:
                "0 20px 60px -10px rgba(5, 242, 155, 0.55), inset 0 0 0 6px rgba(255,255,255,0.08)",
              animation: "checkmarkPop 0.7s cubic-bezier(0.2,0.9,0.4,1.2) both",
            }}
          >
            <CheckCircle2 className="w-14 h-14 text-[#031611]" strokeWidth={2.4} />
          </div>
        </div>

        {/* Title & body */}
        <h1
          className="fade-up font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-center tracking-tight max-w-3xl"
          style={{ animationDelay: "0.2s" }}
          data-testid="thankyou-title"
        >
          {title}
          <span className="text-[#05F29B]"> 🎉</span>
        </h1>

        <p
          className="fade-up mt-4 text-lg sm:text-xl text-white/80 text-center font-medium max-w-xl"
          style={{ animationDelay: "0.35s" }}
          data-testid="thankyou-subtitle"
        >
          {subtitle}
        </p>

        <p
          className="fade-up mt-6 text-base sm:text-lg text-white/70 text-center max-w-xl leading-relaxed"
          style={{ animationDelay: "0.5s" }}
          data-testid="thankyou-body"
        >
          {body}
        </p>

        {/* Token */}
        {cfg?.show_token && token && (
          <div
            className="fade-up mt-10 w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-5"
            style={{ animationDelay: "0.65s" }}
            data-testid="token-card"
          >
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-white/50 mb-2">
              <ShieldCheck className="w-3.5 h-3.5 text-[#05F29B]" />
              Token de autenticidade
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p
                  className="font-mono text-base sm:text-lg font-semibold text-white tracking-wider truncate"
                  data-testid="token-short"
                >
                  #{shortCode}
                </p>
                <p
                  className="font-mono text-[10px] text-white/40 truncate mt-1"
                  data-testid="token-full"
                >
                  {token}
                </p>
              </div>
              <button
                onClick={() => copy(token)}
                className="shrink-0 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition p-2"
                aria-label="Copiar token"
                data-testid="copy-token-btn"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            {copied && (
              <p className="text-[11px] text-[#05F29B] mt-2 font-medium">
                ✓ Token copiado!
              </p>
            )}
            <p className="text-[10px] text-white/40 mt-3 leading-snug">
              Este código é único, gerado no momento da abertura desta página e serve como comprovação de autenticidade.
            </p>
          </div>
        )}

        {/* WhatsApp button */}
        {waUrl ? (
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-btn"
            className="fade-up relative mt-12 inline-flex items-center gap-3 px-8 py-5 rounded-full text-[#052018] font-bold text-base sm:text-lg overflow-hidden group"
            style={{
              background: "linear-gradient(135deg, #25D366 0%, #0bbf56 100%)",
              animation: "fadeUp 0.7s 0.85s both, wabtnPulse 2.2s 1.6s infinite, wabtnBob 2.8s 1.6s ease-in-out infinite",
            }}
          >
            <span className="relative z-10 inline-flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-white/95 grid place-items-center shadow-sm">
                <MessageCircle className="w-5 h-5 text-[#25D366]" fill="#25D366" strokeWidth={0} />
              </span>
              {buttonText}
              <Sparkles className="w-4 h-4 opacity-80" />
            </span>
            {/* shine effect */}
            <span
              className="pointer-events-none absolute inset-y-0 w-24 bg-white/30 blur-lg"
              style={{
                animation: "shine 2.6s 2s infinite",
              }}
            />
          </a>
        ) : (
          <div
            className="fade-up mt-12 rounded-full border border-amber-400/30 bg-amber-400/10 text-amber-300 text-sm px-5 py-3"
            style={{ animationDelay: "0.85s" }}
          >
            Configure seu WhatsApp em Configurações → Página de Obrigado.
          </div>
        )}

        <p
          className="fade-up mt-10 text-xs text-white/40 text-center max-w-md"
          style={{ animationDelay: "1s" }}
        >
          Dúvidas? Entre em contato pelo WhatsApp acima. Guarde seu token para referência.
        </p>
      </div>
    </div>
  );
}
